import type { InventoryVehicle } from '@carvana/merch-contracts';
import { InventoryTypeId } from '@carvana/merch-contracts/InventoryType';
import MerchTile, { MoreDetailsOptions } from '@carvana/merch-tile';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { assetsCdnUrl, vexCdnUrl } from '~/constants/environment';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useIsMobile } from '~/context/common/DeviceContext';
import { useFactoryUpgradesQuali } from '~/context/common/FactoryUpgradesQualiContext';
import { useConsumer, useConfiguration } from '~/context/common/StaticContext';
import TradeContext from '~/context/common/TradeContext';
import {
  useFavoritesIds,
  useUserLocation,
  useIsLoggedIn
} from '~/context/common/UserContext';
import { useIsDealershipPage } from '~/context/dealership/StaticContext';
import { useHasSoftPull } from '~/context/srp/FinanceContext';
import {
  useInventoryBulkData,
  useSearchRequestId
} from '~/context/srp/InventoryContext';
import ModalContext from '~/context/srp/ModalContext';
import { useShouldUpdateTerms } from '~/context/srp/PricingContext';
import { useShowFinance } from '~/context/srp/SearchContext';
import { useToggleFavorite } from '~/hooks/common/favorites';
import useShowTradeIncentives from '~/hooks/common/selectors/useShowTradeIncentives';
import { useHasActiveTradeApplied } from '~/hooks/srp/selectors';
import useSendTileInfoEvent from './useSendTileInfoEvent';
import useSetSearchRequestIdCookie from './useSetSearchRequestIdCookie';

type OwnProps = {
  highPriorityImage?: boolean;
  vehicle: InventoryVehicle;
};

export default function VehicleTile({ highPriorityImage, vehicle }: OwnProps) {
  const isMobile = useIsMobile();
  const hasSoftPull = useHasSoftPull();
  const favoriteIds = useFavoritesIds();
  const hasTradeInApplied = useHasActiveTradeApplied();
  const { captureEngagement } = useFactoryUpgradesQuali();
  const [ref, inView] = useInView({
    root: null,
    rootMargin: isMobile ? '-49% 0px -49% 0px' : '90px 0px 0px', // Vehicle tile is intersecting the middle 2% of the viewport
    threshold: isMobile ? 0 : 0.5
  });
  const searchRequestId = useSearchRequestId();
  const userLocation = useUserLocation();
  const { setWipNotificationModalOpen, setMoreDetailsModalOpen } =
    useContext(ModalContext);
  const trackEvent = useTrackEvent();
  const termsAreUpdating = useShouldUpdateTerms();
  const { isTradeInApplied } = useContext(TradeContext);

  const toggleFavorite = useToggleFavorite({
    onAddFavorite: () => {
      if (vehicle.vehicleInventoryType === InventoryTypeId.WIP) {
        setWipNotificationModalOpen(true, { vehicleId: vehicle.vehicleId });
      }
    }
  });
  const isLoggedIn = useIsLoggedIn();
  const [isFavoritedLocally, setFavoritedLocally] = useState(
    favoriteIds.includes(vehicle.vehicleId)
  );
  const showFinanceInfo = useShowFinance();
  const inventoryBulkData = useInventoryBulkData();
  const isDealership = useIsDealershipPage();
  const showTradeIncentives = useShowTradeIncentives();

  const setSearchRequestCookieId = useSetSearchRequestIdCookie();
  const sendTileInfoEvent = useSendTileInfoEvent();

  const consumer = useConsumer();

  function handleOnClick() {
    setSearchRequestCookieId();
    sendTileInfoEvent(vehicle);
  }

  const handleFavorite = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const { vehicleId, stockNumber } = vehicle;

      toggleFavorite({ vehicleId, stockNumber });
      if (isLoggedIn) setFavoritedLocally(v => !v);
      trackEvent('Vehicle Tile', {
        action: 'clicked',
        actionTaken: isFavoritedLocally ? 'removed' : 'added',
        item: 'favorite heart',
        vehicleId
      });
    },
    [isFavoritedLocally, isLoggedIn, toggleFavorite, trackEvent, vehicle]
  );

  useEffect(() => {
    setFavoritedLocally(favoriteIds.includes(vehicle.vehicleId));
  }, [favoriteIds, vehicle.vehicleId]);

  const openModal = useCallback(
    (options: MoreDetailsOptions) => {
      setMoreDetailsModalOpen(true, options);
    },
    [setMoreDetailsModalOpen]
  );

  return (
    <MerchTile
      assets={{
        cvnaVexFastlyImageUrl: vexCdnUrl,
        cvnaAppAssetsCdn: assetsCdnUrl
      }}
      availabilityInfo={{
        pickup:
          inventoryBulkData[vehicle.vehicleId]?.soonestPickup.content ??
          undefined,
        delivery:
          inventoryBulkData[vehicle.vehicleId]?.soonestDelivery.content ??
          undefined
      }}
      consumer={consumer}
      coreData={{
        ...vehicle,
        price: vehicle.price
          ? { ...vehicle.price, totalWithDiscounts: vehicle.price.total }
          : undefined
      }}
      financeInfo={
        vehicle.price
          ? { ...vehicle.price, termInMonths: vehicle.price.term }
          : undefined
      }
      hasBorder
      highPriorityImage={highPriorityImage}
      inVerticalStack={isMobile}
      inView={inView}
      isDealership={isDealership}
      isFavorite={isFavoritedLocally}
      ref={ref}
      showFinanceInfo={showFinanceInfo}
      showSplatting={true}
      tradeIncentive={
        showTradeIncentives ? vehicle.price?.tradeIncentive : undefined
      }
      userInfo={{
        locationState: userLocation.state,
        hasSoftPull,
        tradeInApplied: hasTradeInApplied
      }}
      utils={{
        handleFavoriteClick: handleFavorite,
        handleTileClick: handleOnClick,
        onFactoryUpgradesEngaged: captureEngagement,
        openMoreDetailsModal: openModal,
        trackEvent,
        termsUpdateState: {
          termsAreUpdating,
          tradeUpdateState: isTradeInApplied
        },
        searchRequestId
      }}
    />
  );
}
